/**
 * 10.9BC — ARENA 3v3 (fila aleatória + ELIMINAÇÃO, sem respawn).
 *
 * Mesma linguagem do 1x1 (DuelArena3D): overlay com cena/câmera/renderer próprios, Caçadores 3D,
 * joystick, ataque automático no INIMIGO mais perto ao alcance. Diferenças:
 *   · 6 lutadores (3 azuis = time A, 3 vermelhos = time B), cada cliente DONO da própria vida;
 *   · "hit" é direcionado (to = id) — só o alvo desconta;
 *   · vitória = os 3 do outro time com vida 0 (ou que saíram/caíram da rede);
 *   · câmera no centro dos vivos, altura cresce com o espalhamento.
 * Relay/casual (cheatável) como o 1x1 — servidor autoritativo é upgrade futuro.
 */
import * as THREE from "three";
import { closeTeamChannel, openTeamChannel, sendTeamHit, sendTeamState, type TeamMember, type TeamRoom, type TeamState } from "../net/CloudDuel";
import { buildWeaponMesh } from "../player/Player";
import type { DuelFighter, PvpStats } from "./DuelArena3D";

const ARENA_R = 16;
const HP = 100;
const MOVE_SPEED = 6.2;
const ATTACK_RANGE = 3.2;
const ATTACK_CD = 0.55;
const START_WAIT = 12; // s esperando os outros (quem não chegou entra como caído)
const NET_TIMEOUT = 8000; // ms sem estado = caiu
const PVP_HP_MULT = 16; // 10.9FH — vida real x isto (igual ao 1x1)
const PVP_MIN_TAKEN = 0.2; // o golpe nunca cai abaixo de 20% (anti-imortal)

interface Fighter {
  m: TeamMember;
  group: THREE.Group;
  slash: THREE.MeshBasicMaterial;
  bar: HTMLElement;
  st: TeamState; // último estado (o meu é a fonte da verdade local)
  render: THREE.Vector3;
  seen: number; // performance.now() do último estado (0 = nunca)
  dead: boolean;
  skinned?: boolean; // 10.9HK — já trocou o avatar pela arma de verdade dele
  ally?: boolean;
}

export class TeamArena3D {
  private readonly root: HTMLDivElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly statusEl: HTMLElement;
  private readonly scoreEl: HTMLElement;
  private readonly barsEl: HTMLElement;
  private renderer: THREE.WebGLRenderer | null = null;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(52, 1, 0.1, 220);
  private fighters: Fighter[] = [];
  private myLook: { v: string; e?: number; t?: number; f?: string } | null = null; // 10.9HK
  private me: Fighter | null = null;
  private floatsEl!: HTMLElement; // 10.9FP
  private myTeam: "A" | "B" = "A";
  private myDmg = 6;
  private st: PvpStats | null = null; // 10.9FH — atributos reais
  private myMax = HP;
  private atkCd = ATTACK_CD;
  private range = ATTACK_RANGE;
  private spellCd = [0, 0, 0];
  private buffT = 0;
  private buff: NonNullable<PvpStats["spells"][number]["buff"]> | null = null;
  private fields: { x: number; z: number; t: number; every: number; acc: number; r: number; dmg: number; follow: boolean; drain: number }[] = [];
  private traps: { x: number; z: number; t: number; trig: number; r: number; dmg: number }[] = [];
  private meteors: { x: number; z: number; t: number; r: number; dmg: number }[] = [];
  private fx: { m: THREE.Mesh; t: number; dur: number; grow: number }[] = [];
  private spellBtns: HTMLButtonElement[] = [];
  private phase: "waiting" | "countdown" | "fighting" | "over" = "waiting";
  private countdown = 3;
  private waitTimer = 0;
  private moveDir = new THREE.Vector2();
  private atkTimer = 0;
  private netTimer = 0;
  private slashT = 0;
  private over = false;
  private raf = 0;
  private last = 0;

  constructor(
    uiRoot: HTMLElement,
    private readonly onExit: () => void,
    private readonly onResult: (won: boolean) => void,
    private readonly onBack: () => void,
  ) {
    injectStyles();
    this.root = document.createElement("div");
    this.root.className = "overlay d3-overlay t3-overlay";
    this.root.hidden = true;
    this.root.innerHTML = `
      <canvas class="d3-canvas"></canvas>
      <div class="d3-hud">
        <div class="d3-top">
          <div class="d3-status"></div>
          <button class="btn d3-x" data-a="close">Sair</button>
        </div>
        <div class="t3-score"></div>
      </div>
      <div class="t3-bars"></div>
      <div class="d3-stick"><div class="d3-knob"></div></div>
      <div class="d3-floats"></div>
      <div class="d3-spells"></div>`;
    uiRoot.appendChild(this.root);
    this.canvas = this.root.querySelector(".d3-canvas")!;
    this.statusEl = this.root.querySelector(".d3-status")!;
    this.scoreEl = this.root.querySelector(".t3-score")!;
    this.barsEl = this.root.querySelector(".t3-bars")!;
    this.floatsEl = this.root.querySelector(".d3-floats")!;
    this.root.querySelector('[data-a="close"]')!.addEventListener("click", () => {
      if (this.phase === "over") { this.hide(); this.onBack(); } else this.exit();
    });
    this.buildScene();
    this.bindStick();
  }

  private buildScene(): void {
    this.scene.background = new THREE.Color(0x07070f);
    this.scene.fog = new THREE.Fog(0x07070f, 24, 60);
    this.scene.add(new THREE.AmbientLight(0x8895c8, 1.1));
    const dir = new THREE.DirectionalLight(0xffffff, 1.1);
    dir.position.set(6, 16, 8);
    this.scene.add(dir);
    const floor = new THREE.Mesh(new THREE.CircleGeometry(ARENA_R, 56), new THREE.MeshStandardMaterial({ color: 0x14172b, roughness: 0.95, metalness: 0.1 }));
    floor.rotation.x = -Math.PI / 2;
    this.scene.add(floor);
    const ring = new THREE.Mesh(new THREE.TorusGeometry(ARENA_R, 0.3, 8, 72), new THREE.MeshBasicMaterial({ color: 0x7c4dff }));
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.1;
    this.scene.add(ring);
    // metades: azul (A, z>0) e vermelha (B, z<0) — linha central
    const mid = new THREE.Mesh(new THREE.BoxGeometry(ARENA_R * 2, 0.02, 0.12), new THREE.MeshBasicMaterial({ color: 0x3a3560 }));
    mid.position.y = 0.03;
    this.scene.add(mid);
    for (const [z, c] of [[ARENA_R * 0.55, 0x2be0ff], [-ARENA_R * 0.55, 0xff5a6a]] as [number, number][]) {
      const g = new THREE.Mesh(new THREE.RingGeometry(5.6, 6, 40), new THREE.MeshBasicMaterial({ color: c, transparent: true, opacity: 0.28, side: THREE.DoubleSide }));
      g.rotation.x = -Math.PI / 2;
      g.position.set(0, 0.04, z);
      this.scene.add(g);
    }
    window.addEventListener("resize", () => { if (!this.root.hidden) this.resize(); });
  }

  private buildAvatar(color: number, visualId = "sword", tier?: number, family?: string): { group: THREE.Group; slash: THREE.MeshBasicMaterial } {
    const g = new THREE.Group();
    const cloakMat = new THREE.MeshStandardMaterial({ color: 0x1b1630, emissive: color, emissiveIntensity: 0.18, roughness: 0.85, flatShading: true });
    const armorMat = new THREE.MeshStandardMaterial({ color: 0x3a3358, emissive: color, emissiveIntensity: 0.25, roughness: 0.45, metalness: 0.5, flatShading: true });
    const accent = new THREE.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: 0.9, roughness: 0.3, flatShading: true });
    const body = new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.58, 1.15, 10), cloakMat);
    body.position.y = 0.62;
    g.add(body);
    const torso = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.55, 0.42), armorMat);
    torso.position.y = 1.05;
    g.add(torso);
    for (const s of [-1, 1]) {
      const pad = new THREE.Mesh(new THREE.DodecahedronGeometry(0.2, 0), armorMat);
      pad.position.set(s * 0.4, 1.3, 0);
      pad.scale.set(1, 0.7, 1);
      g.add(pad);
    }
    const hood = new THREE.Mesh(new THREE.ConeGeometry(0.34, 0.55, 8), cloakMat);
    hood.position.y = 1.72;
    g.add(hood);
    const head = new THREE.Mesh(new THREE.SphereGeometry(0.26, 10, 8), armorMat);
    head.position.y = 1.5;
    g.add(head);
    const visor = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.09, 0.14), accent);
    visor.position.set(0, 1.52, 0.22);
    g.add(visor);
    const wm = buildWeaponMesh(visualId, { energy: color, glow: 1.2, tier, family }); // 10.9HG
    wm.scale.multiplyScalar(0.55);
    wm.rotation.z = -0.35;
    wm.position.set(0.42, 0.85, 0.15);
    g.add(wm);
    const slashMat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0, side: THREE.DoubleSide, depthWrite: false });
    const slash = new THREE.Mesh(new THREE.RingGeometry(ATTACK_RANGE * 0.5, ATTACK_RANGE, 20, 1, -0.9, 1.8), slashMat);
    slash.rotation.x = -Math.PI / 2;
    slash.position.y = 0.15;
    g.add(slash);
    return { group: g, slash: slashMat };
  }

  // ---------- ciclo ----------
  open(myId: string, room: TeamRoom, me: DuelFighter): void {
    this.clearFighters();
    this.over = false;
    this.phase = "waiting";
    this.countdown = 3;
    this.waitTimer = 0;
    this.atkTimer = 0;
    this.moveDir.set(0, 0);
    this.root.hidden = false;
    const mine = room.members.find((m) => m.id === myId)!;
    this.myTeam = mine.team;
    // dano relativo ao poder MÉDIO da sala (fallback se o cliente nao mandou atributos)
    const avg = room.members.reduce((a, m) => a + Math.max(1, m.power), 0) / room.members.length;
    this.myDmg = Math.max(2, Math.min(22, Math.round(7 * (Math.max(1, me.power) / avg))));
    // 10.9FH — ATRIBUTOS REAIS (vida, dano, alcance, velocidade, defesa e as 3 magias)
    this.st = me.stats ?? null;
    this.myMax = this.st ? Math.round(this.st.maxHp * PVP_HP_MULT) : HP;
    this.atkCd = this.st ? 1 / Math.max(0.3, this.st.aps) : ATTACK_CD;
    this.range = this.st ? Math.min(10, Math.max(2.6, this.st.range)) : ATTACK_RANGE;
    this.spellCd = [0, 0, 0];
    this.buffT = 0; this.buff = null;
    this.fields.length = 0; this.traps.length = 0; this.meteors.length = 0;

    let ia = 0, ib = 0;
    for (const m of room.members) {
      const isMe = m.id === myId;
      const ally = m.team === this.myTeam;
      const color = isMe ? (me.energy ?? 0x2be0ff) : ally ? 0x4fb8ff : 0xff5a6a;
      const slot = m.team === "A" ? ia++ : ib++;
      const x = (slot - 1) * 4.2;
      const z = (m.team === "A" ? 1 : -1) * ARENA_R * 0.55;
      const av = this.buildAvatar(color, isMe ? me.visualId ?? "sword" : "sword", isMe ? me.weaponTier : undefined, isMe ? me.weaponFamily : undefined); // 10.9HG
      av.group.position.set(x, 0, z);
      this.scene.add(av.group);
      const bar = document.createElement("div");
      bar.className = `d3-bar ${ally ? "t3-ally" : "t3-enemy"}${isMe ? " t3-me" : ""}`;
      bar.innerHTML = `<span class="d3-name">${escapeHtml(isMe ? (me.name || "Você") : m.name)}</span><span class="d3-cp">⚔ ${fmtCP(Math.round(m.power || 0))}</span><span class="d3-hpwrap"><i></i></span>`;
      this.barsEl.appendChild(bar);
      const f: Fighter = { m, group: av.group, slash: av.slash, bar, st: { id: m.id, x, z, hp: HP, maxHp: HP, f: m.team === "A" ? Math.PI : 0 }, render: new THREE.Vector3(x, 0, z), seen: 0, dead: false, ally };
      if (isMe) this.myLook = me.visualId ? { v: me.visualId, e: me.energy, t: me.weaponTier, f: me.weaponFamily } : null; // 10.9HK
      this.fighters.push(f);
      if (isMe) { this.me = f; f.seen = performance.now(); f.st.hp = this.myMax; f.st.maxHp = this.myMax; }
    }
    this.buildSpellButtons();
    this.statusEl.textContent = "Aguardando os times…";
    this.root.querySelector('[data-a="close"]')!.textContent = "Sair";
    this.updateScore();

    if (!this.renderer) {
      this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true, alpha: false, powerPreference: "high-performance" });
      this.renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
    }
    this.resize();

    openTeamChannel(room.topic, myId, {
      onState: (s) => {
        const f = this.fighters.find((x) => x.m.id === s.id);
        if (!f || f === this.me) return;
        if (s.w && !f.skinned) { // 10.9HK — cada um aparece com a arma/skin de verdade (cor segue o time)
          f.skinned = true;
          this.scene.remove(f.group);
          const av = this.buildAvatar(f.ally ? 0x4fb8ff : 0xff5a6a, s.w.v, s.w.t, s.w.f);
          av.group.position.copy(f.group.position);
          av.group.rotation.copy(f.group.rotation);
          f.group = av.group; f.slash = av.slash;
          this.scene.add(av.group);
        }
        f.st = s;
        f.seen = performance.now();
        if (s.hp <= 0 && !f.dead) { f.dead = true; this.updateScore(); }
      },
      onHit: (_from, to, dmg) => { if (this.me && to === this.me.m.id) this.takeDamage(dmg); },
      onLeave: (id) => { const f = this.fighters.find((x) => x.m.id === id); if (f && f !== this.me && !f.dead) { f.dead = true; this.updateScore(); } },
    });
    this.last = performance.now();
    this.loop(this.last);
  }

  private clearFighters(): void {
    for (const f of this.fx) { this.scene.remove(f.m); f.m.geometry.dispose(); (f.m.material as THREE.Material).dispose(); }
    this.fx.length = 0; this.fields.length = 0; this.traps.length = 0; this.meteors.length = 0;
    for (const f of this.fighters) this.scene.remove(f.group);
    this.fighters = [];
    this.me = null;
    this.barsEl.innerHTML = "";
  }

  hide(): void {
    this.root.hidden = true;
    cancelAnimationFrame(this.raf);
    this.raf = 0;
    void closeTeamChannel();
  }

  private exit(): void {
    if (this.me && !this.over) { this.me.st.hp = 0; sendTeamState(this.me.st); }
    this.hide();
    this.onExit();
  }

  private takeDamage(dmg: number): void {
    if (this.over || !this.me || this.me.dead) return;
    if (this.st) { // 10.9FH — armadura + reducao + bloqueio + buff (piso de 20%)
      const raw = dmg;
      const arm = this.st.armor > 0 ? this.st.armor / (this.st.armor + 100) : 0;
      const blocked = Math.random() < Math.min(0.6, this.st.block);
      let d = raw * (1 - arm) * (1 - Math.min(0.8, this.st.dr)) * (1 - (this.buffT > 0 ? this.buff?.dr ?? 0 : 0)) * (blocked ? 0.5 : 1);
      dmg = Math.round(Math.max(raw * PVP_MIN_TAKEN, d));
      this.popRing(this.me.st.x, this.me.st.z, blocked ? 0xffffff : 0xff4d5a, 1.2);
    }
    this.popFloat(this.me.st.x, this.me.st.z, dmg, "taken", false);
    this.me.st.hp = Math.max(0, this.me.st.hp - dmg);
    if (this.me.st.hp <= 0) { this.me.dead = true; sendTeamState(this.me.st); this.updateScore(); this.statusEl.textContent = "💀 Você caiu — torcendo pelo time…"; }
  }

  private alive(team: "A" | "B"): number {
    return this.fighters.filter((f) => f.m.team === team && !f.dead).length;
  }

  private updateScore(): void {
    const a = this.alive("A"), b = this.alive("B");
    this.scoreEl.innerHTML = `<span class="t3-a">🔵 ${a}</span><span class="t3-vs">vs</span><span class="t3-b">${b} 🔴</span>`;
  }

  private finish(won: boolean, reason?: string): void {
    if (this.over) return;
    this.over = true;
    this.phase = "over";
    this.updateScore();
    this.statusEl.innerHTML = won ? `<b class="d3-win">🏆 Seu time venceu!</b>${reason ? " " + reason : ""}` : `<b class="d3-lose">Seu time perdeu…</b>`;
    this.onResult(won);
    this.root.querySelector('[data-a="close"]')!.textContent = "🔁 Voltar ao lobby";
    void closeTeamChannel();
  }

  // ---------- 10.9FH — combate real (espelha o 1x1) ----------
  private hitEnemy(target: Fighter, mult: number): void {
    if (!this.st) { sendTeamHit(this.me!.m.id, target.m.id, this.myDmg); return; }
    const critCh = this.st.crit + (this.buffT > 0 ? this.buff?.critChance ?? 0 : 0);
    const crit = Math.random() < critCh;
    const dmg = this.st.dmg * mult * (crit ? Math.max(1, this.st.critMult) : 1) * (1 + (this.buffT > 0 ? this.buff?.dmgMult ?? 0 : 0));
    sendTeamHit(this.me!.m.id, target.m.id, Math.round(dmg));
    if (this.st.lifeSteal > 0 && this.me) this.me.st.hp = Math.min(this.myMax, this.me.st.hp + dmg * this.st.lifeSteal * 0.5);
    this.popRing(target.render.x, target.render.z, crit ? 0xffd24a : 0xffffff, crit ? 1.6 : 1);
    this.popFloat(target.render.x, target.render.z, Math.round(dmg), "dealt", crit);
  }
  private enemiesInRadius(x: number, z: number, r: number): Fighter[] {
    return this.fighters.filter((f) => f.m.team !== this.myTeam && !f.dead && Math.hypot(f.render.x - x, f.render.z - z) <= r + 0.5);
  }
  private buildSpellButtons(): void {
    const box = this.root.querySelector<HTMLElement>(".d3-spells")!;
    const sp = this.st?.spells ?? [];
    box.innerHTML = sp.map((s, i) => `<button class="d3-sp" data-i="${i}" style="--c:#${s.color.toString(16).padStart(6, "0")}"><span>${s.emoji}</span><i class="d3-cd"></i></button>`).join("");
    this.spellBtns = [...box.querySelectorAll<HTMLButtonElement>(".d3-sp")];
    for (const b of this.spellBtns) b.addEventListener("pointerdown", (e) => { e.stopPropagation(); e.preventDefault(); this.cast(Number(b.dataset.i)); });
  }
  private cast(i: number): void {
    const s = this.st?.spells[i];
    const me = this.me;
    if (!s || !me || me.dead || this.phase !== "fighting" || this.over || this.spellCd[i] > 0) return;
    const tgt = this.nearestEnemy();
    const k = s.kind;
    // magias miradas precisam de alvo; buff/area-no-lugar nao
    const aimed = k === "dash" || k === "wave" || k === "volley" || k === "chain" || k === "meteor" || k === "summon" || k === "slam" || k === "nova";
    if (aimed && !tgt) return;
    this.spellCd[i] = s.cooldown;
    this.slashT = 0.22;
    let dx = 0, dz = 1, ad = 0;
    if (tgt) { const ax = tgt.render.x - me.st.x, az = tgt.render.z - me.st.z; ad = Math.hypot(ax, az) || 1; dx = ax / ad; dz = az / ad; me.st.f = Math.atan2(dx, dz); }
    if (k === "buff" && s.buff) {
      this.buff = s.buff; this.buffT = s.buff.seconds;
      if (s.buff.healFrac) me.st.hp = Math.min(this.myMax, me.st.hp + this.myMax * s.buff.healFrac);
      this.popRing(me.st.x, me.st.z, s.color, 2.6);
    } else if (k === "dash") {
      const reach = Math.min(s.dashRange ?? 7, ad + 1.5);
      const nx = me.st.x + dx * reach, nz = me.st.z + dz * reach, d = Math.hypot(nx, nz), lim = ARENA_R - 0.6;
      me.st.x = d > lim ? (nx / d) * lim : nx; me.st.z = d > lim ? (nz / d) * lim : nz;
      for (const e of this.enemiesInRadius(me.st.x, me.st.z, s.radius)) this.hitEnemy(e, s.dmgMult);
      this.popRing(me.st.x, me.st.z, s.color, s.radius);
    } else if (k === "curse" || k === "whirl") {
      const fx = me.st.x + dx * (s.ahead ?? 0), fz = me.st.z + dz * (s.ahead ?? 0);
      const f = s.field ?? { seconds: 4, tickEvery: 0.5 };
      this.fields.push({ x: fx, z: fz, t: f.seconds, every: f.tickEvery, acc: 0, r: s.radius, dmg: s.dmgMult, follow: k === "whirl", drain: s.drainPct ?? 0 });
      this.popRing(fx, fz, s.color, s.radius);
    } else if (k === "trap" && s.trap) {
      this.traps.push({ x: me.st.x, z: me.st.z, t: s.trap.seconds, trig: s.trap.trigger, r: s.radius, dmg: s.dmgMult });
      this.popRing(me.st.x, me.st.z, s.color, s.trap.trigger);
    } else if (k === "meteor") {
      const reach = Math.min(s.meteorRange ?? 8, ad);
      this.meteors.push({ x: me.st.x + dx * reach, z: me.st.z + dz * reach, t: s.meteorDelay ?? 0.9, r: s.radius, dmg: s.dmgMult });
      this.popRing(me.st.x + dx * reach, me.st.z + dz * reach, s.color, s.radius);
    } else if (k === "wave") {
      const len = s.waveLength ?? 12;
      for (const e of this.fighters) {
        if (e.m.team === this.myTeam || e.dead) continue;
        const rx = e.render.x - me.st.x, rz = e.render.z - me.st.z, along = rx * dx + rz * dz;
        if (along >= -0.5 && along <= len && Math.abs(rx * dz - rz * dx) <= s.radius) this.hitEnemy(e, s.dmgMult);
      }
      for (let d = 1.5; d <= len; d += 2) this.popRing(me.st.x + dx * d, me.st.z + dz * d, s.color, s.radius * 0.8);
    } else if (k === "summon") {
      for (let j = 0; j < 3; j++) window.setTimeout(() => { const t2 = this.nearestEnemy(); if (!this.over && t2) this.hitEnemy(t2, Math.max(0.6, s.dmgMult)); }, j * 350);
      this.popRing(me.st.x, me.st.z, s.color, 2.2);
    } else { // slam / nova / volley / chain
      const reach = k === "volley" || k === "chain" ? Math.max(s.radius, 10) : s.radius;
      for (const e of this.enemiesInRadius(me.st.x, me.st.z, reach)) this.hitEnemy(e, s.dmgMult);
      if (tgt && (k === "volley" || k === "chain")) { this.hitEnemy(tgt, s.dmgMult); this.popRing(tgt.render.x, tgt.render.z, s.color, 2.2); }
      else this.popRing(me.st.x, me.st.z, s.color, reach);
    }
  }
  private updateSpellEffects(dt: number): void {
    if (!this.me) return;
    for (let i = this.fields.length - 1; i >= 0; i--) {
      const f = this.fields[i];
      f.t -= dt; f.acc += dt;
      if (f.follow) { f.x = this.me.st.x; f.z = this.me.st.z; }
      if (f.acc >= f.every) {
        f.acc = 0;
        const hit = this.enemiesInRadius(f.x, f.z, f.r);
        for (const e of hit) this.hitEnemy(e, f.dmg);
        if (f.drain > 0 && hit.length > 0) this.me.st.hp = Math.min(this.myMax, this.me.st.hp + this.myMax * f.drain * Math.min(3, hit.length));
      }
      if (f.t <= 0) this.fields.splice(i, 1);
    }
    for (let i = this.traps.length - 1; i >= 0; i--) {
      const t = this.traps[i];
      t.t -= dt;
      const hit = this.enemiesInRadius(t.x, t.z, t.trig);
      if (hit.length > 0) { for (const e of this.enemiesInRadius(t.x, t.z, t.r)) this.hitEnemy(e, t.dmg); this.popRing(t.x, t.z, 0xff9a3c, t.r); this.traps.splice(i, 1); }
      else if (t.t <= 0) this.traps.splice(i, 1);
    }
    for (let i = this.meteors.length - 1; i >= 0; i--) {
      const m = this.meteors[i];
      m.t -= dt;
      if (m.t <= 0) { for (const e of this.enemiesInRadius(m.x, m.z, m.r)) this.hitEnemy(e, m.dmg); this.popRing(m.x, m.z, 0xd15bff, m.r * 1.3); this.meteors.splice(i, 1); }
    }
  }
  private popFloat(x: number, z: number, amount: number, kind: "dealt" | "taken", crit: boolean): void {
    if (amount <= 0 || !this.renderer) return;
    const v = new THREE.Vector3(x, 1.9, z).project(this.camera);
    if (v.z > 1) return;
    const el = document.createElement("div");
    el.className = `d3-float ${kind}${crit ? " crit" : ""}`;
    el.textContent = (kind === "taken" ? "-" : "") + fmtCP(Math.round(amount)) + (crit ? "!" : "");
    el.style.left = `${(v.x * 0.5 + 0.5) * this.root.clientWidth}px`;
    el.style.top = `${(-v.y * 0.5 + 0.5) * this.root.clientHeight}px`;
    this.floatsEl.appendChild(el);
    window.setTimeout(() => el.remove(), 850);
  }
  private popRing(x: number, z: number, color: number, r: number): void {
    const m = new THREE.Mesh(new THREE.RingGeometry(0.7, 1, 28), new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.85, side: THREE.DoubleSide, depthWrite: false }));
    m.rotation.x = -Math.PI / 2;
    m.position.set(x, 0.14, z);
    this.scene.add(m);
    this.fx.push({ m, t: 0, dur: 0.45, grow: Math.max(1, r) });
  }
  private updateFx(dt: number): void {
    for (let i = this.fx.length - 1; i >= 0; i--) {
      const f = this.fx[i];
      f.t += dt;
      const k = f.t / f.dur, sc = 0.4 + k * f.grow;
      f.m.scale.set(sc, sc, sc);
      (f.m.material as THREE.MeshBasicMaterial).opacity = 0.85 * (1 - k);
      if (f.t >= f.dur) { this.scene.remove(f.m); f.m.geometry.dispose(); (f.m.material as THREE.Material).dispose(); this.fx.splice(i, 1); }
    }
    const sp = this.st?.spells ?? [];
    this.spellBtns.forEach((b, i) => {
      const cd = sp[i] ? this.spellCd[i] / sp[i].cooldown : 0;
      (b.querySelector(".d3-cd") as HTMLElement).style.transform = `scaleY(${cd})`;
      b.classList.toggle("ready", cd <= 0 && this.phase === "fighting" && !this.me?.dead);
    });
  }

  // ---------- input ----------
  private bindStick(): void {
    const stick = this.root.querySelector(".d3-stick") as HTMLElement;
    const knob = this.root.querySelector(".d3-knob") as HTMLElement;
    let id = -1, cx = 0, cy = 0;
    const R = 46;
    const move = (e: PointerEvent): void => {
      if (e.pointerId !== id) return;
      let dx = e.clientX - cx, dy = e.clientY - cy;
      const d = Math.hypot(dx, dy) || 1;
      const cl = Math.min(1, d / R);
      dx /= d; dy /= d;
      knob.style.transform = `translate(${dx * R * cl}px, ${dy * R * cl}px)`;
      this.moveDir.set(dx * cl, dy * cl);
    };
    const end = (e: PointerEvent): void => {
      if (e.pointerId !== id) return;
      id = -1; this.moveDir.set(0, 0);
      knob.style.transform = "translate(0,0)";
    };
    this.root.addEventListener("pointerdown", (e) => {
      if ((e.target as HTMLElement).closest(".btn, .d3-sp")) return;
      id = e.pointerId;
      const r = stick.getBoundingClientRect();
      cx = r.left + r.width / 2; cy = r.top + r.height / 2;
      move(e);
    });
    this.root.addEventListener("pointermove", move);
    this.root.addEventListener("pointerup", end);
    this.root.addEventListener("pointercancel", end);
  }

  private resize(): void {
    const w = this.root.clientWidth || window.innerWidth;
    const h = this.root.clientHeight || window.innerHeight;
    this.renderer?.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  // ---------- loop ----------
  private loop = (now: number): void => {
    const dt = Math.min(0.05, (now - this.last) / 1000);
    this.last = now;
    if (!this.over) this.step(dt);
    this.render(dt);
    this.raf = requestAnimationFrame(this.loop);
  };

  private moveMe(dt: number): void {
    const me = this.me;
    if (!me || me.dead || this.moveDir.lengthSq() < 0.0004) return;
    const nx = me.st.x + this.moveDir.x * MOVE_SPEED * dt;
    const nz = me.st.z + this.moveDir.y * MOVE_SPEED * dt;
    const d = Math.hypot(nx, nz);
    const lim = ARENA_R - 0.6;
    me.st.x = d > lim ? (nx / d) * lim : nx;
    me.st.z = d > lim ? (nz / d) * lim : nz;
    me.st.f = Math.atan2(this.moveDir.x, this.moveDir.y);
  }

  /** Inimigo vivo mais perto de mim (ou null). */
  private nearestEnemy(): Fighter | null {
    const me = this.me;
    if (!me) return null;
    let best: Fighter | null = null, bd = Infinity;
    for (const f of this.fighters) {
      if (f.m.team === this.myTeam || f.dead) continue;
      const d = Math.hypot(f.st.x - me.st.x, f.st.z - me.st.z);
      if (d < bd) { bd = d; best = f; }
    }
    return best;
  }

  step(dt: number): void {
    const me = this.me;
    if (!me) return;
    this.netTimer -= dt;
    if (this.netTimer <= 0) {
      this.netTimer = 0.08;
      sendTeamState({ ...me.st, a: this.slashT > 0.12, w: this.myLook ?? undefined });
    }
    const t = Math.min(1, dt * 12);
    for (const f of this.fighters) {
      if (f === me) { f.render.set(f.st.x, 0, f.st.z); continue; }
      f.render.x += (f.st.x - f.render.x) * t;
      f.render.z += (f.st.z - f.render.z) * t;
    }
    if (this.slashT > 0) this.slashT -= dt;

    if (this.phase === "waiting") {
      this.waitTimer += dt;
      const seenAll = this.fighters.every((f) => f.seen > 0);
      if (seenAll || this.waitTimer > START_WAIT) {
        // quem não apareceu entra como caído (não trava a partida)
        for (const f of this.fighters) if (f.seen === 0) f.dead = true;
        this.phase = "countdown";
        this.countdown = 3;
        this.statusEl.textContent = `🔵 Time ${this.myTeam === "A" ? "Azul (você)" : "Azul"}  vs  Time ${this.myTeam === "B" ? "Vermelho (você)" : "Vermelho"} 🔴`;
        this.updateScore();
        if (this.alive(this.myTeam === "A" ? "B" : "A") === 0) this.finish(true, "O outro time não apareceu.");
      }
      return;
    }
    if (this.phase === "countdown") {
      this.moveMe(dt);
      this.countdown -= dt;
      if (this.countdown <= 0) this.phase = "fighting";
      return;
    }
    // fighting
    this.moveMe(dt);
    this.atkTimer -= dt;
    for (let i = 0; i < 3; i++) this.spellCd[i] = Math.max(0, this.spellCd[i] - dt);
    if (this.buffT > 0) { this.buffT -= dt; if (this.buffT <= 0) this.buff = null; }
    this.updateSpellEffects(dt);
    if (!me.dead) {
      const tgt = this.nearestEnemy();
      if (tgt) {
        const d = Math.hypot(tgt.st.x - me.st.x, tgt.st.z - me.st.z);
        if (d <= this.range && this.atkTimer <= 0) {
          const spd = 1 + (this.buffT > 0 ? this.buff?.atkSpeedMult ?? 0 : 0);
          this.atkTimer = this.atkCd / spd;
          this.slashT = 0.22;
          me.st.f = Math.atan2(tgt.st.x - me.st.x, tgt.st.z - me.st.z);
          this.hitEnemy(tgt, 1);
        }
      }
    }
    // quem sumiu da rede cai
    const now = performance.now();
    let changed = false;
    for (const f of this.fighters) {
      if (f === me || f.dead) continue;
      if (now - f.seen > NET_TIMEOUT) { f.dead = true; changed = true; }
    }
    if (changed) this.updateScore();
    const enemyTeam = this.myTeam === "A" ? "B" : "A";
    if (this.alive(enemyTeam) === 0) this.finish(true);
    else if (this.alive(this.myTeam) === 0) this.finish(false);
  }

  render(dt: number): void {
    if (!this.renderer || !this.me) return;
    let cx = 0, cz = 0, n = 0, spread = 0;
    for (const f of this.fighters) {
      f.group.position.set(f.render.x, f.dead ? -0.9 : 0, f.render.z);
      f.group.rotation.y = f.st.f ?? 0;
      f.group.rotation.x = f.dead ? 0.9 : (f === this.me ? (this.slashT > 0 ? 0.25 : 0) : f.st.a ? 0.25 : 0);
      f.slash.opacity = f === this.me ? Math.max(0, this.slashT / 0.22) * 0.8 : f.st.a ? 0.7 : 0;
      f.group.visible = !f.dead || f.group.position.y > -0.85;
      if (!f.dead) { cx += f.render.x; cz += f.render.z; n++; }
    }
    if (n > 0) { cx /= n; cz /= n; }
    for (const f of this.fighters) if (!f.dead) spread = Math.max(spread, Math.hypot(f.render.x - cx, f.render.z - cz));
    // câmera: centro dos vivos puxado 40% pra mim; altura pelo espalhamento
    const mx = cx * 0.6 + this.me.render.x * 0.4, mz = cz * 0.6 + this.me.render.z * 0.4;
    const h = Math.min(40, Math.max(22, 14 + spread * 1.6));
    const camTarget = new THREE.Vector3(mx, 0.5, mz + 1.5);
    const camPos = new THREE.Vector3(mx, h, mz + h * 0.22);
    this.camera.position.lerp(camPos, Math.min(1, dt * 6));
    this.camera.lookAt(camTarget);
    this.updateFx(dt); // 10.9FH
    this.renderer.render(this.scene, this.camera);
    // barras
    const w = this.root.clientWidth, hh = this.root.clientHeight;
    for (const f of this.fighters) {
      const v = new THREE.Vector3(f.render.x, 2.1, f.render.z).project(this.camera);
      const vis = v.z < 1 && !f.dead;
      f.bar.style.display = vis ? "flex" : "none";
      if (!vis) continue;
      f.bar.style.left = `${(v.x * 0.5 + 0.5) * w}px`;
      f.bar.style.top = `${(-v.y * 0.5 + 0.5) * hh}px`;
      (f.bar.querySelector(".d3-hpwrap i") as HTMLElement).style.transform = `scaleX(${Math.max(0, Math.min(1, f.st.hp / (f.st.maxHp || HP)))})`;
      f.bar.classList.toggle("t3-down", f.dead);
    }
  }
}

function fmtCP(n: number): string { return n >= 1000 ? (n / 1000).toFixed(n >= 10000 ? 0 : 1) + "K" : String(n); } // 10.9FP
function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] ?? c);
}

let stylesInjected = false;
function injectStyles(): void {
  if (stylesInjected || typeof document === "undefined") return;
  stylesInjected = true;
  const css = `
  .t3-score{position:absolute;top:calc(env(safe-area-inset-top,0px) + 48px);left:0;right:0;display:flex;justify-content:center;gap:10px;font-weight:900;font-size:18px;text-shadow:0 2px 6px #000}
  .t3-a{color:#4fb8ff}.t3-b{color:#ff7a86}.t3-vs{color:#9aa7d6;font-size:12px;align-self:center}
  .t3-bars{position:absolute;inset:0;pointer-events:none}
  .t3-ally .d3-hpwrap i{background:linear-gradient(90deg,#4fb8ff,#9fe6ff)}
  .t3-me .d3-hpwrap i{background:linear-gradient(90deg,#38f5a0,#8affc0)}
  .t3-enemy .d3-hpwrap i{background:linear-gradient(90deg,#ff5a6a,#ff9a9a)}
  .t3-down{opacity:.4;filter:grayscale(1)}
  .d3-spells{position:absolute;right:calc(env(safe-area-inset-right,0px) + 14px);bottom:calc(env(safe-area-inset-bottom,0px) + 34px);display:flex;flex-direction:column;gap:10px;pointer-events:auto}
  .d3-sp{position:relative;width:60px;height:60px;border-radius:50%;border:3px solid var(--c);background:rgba(10,12,26,.8);font-size:25px;overflow:hidden;opacity:.6;touch-action:none}
  .d3-sp.ready{opacity:1;box-shadow:0 0 14px var(--c)}
  .d3-sp .d3-cd{position:absolute;left:0;right:0;bottom:0;height:100%;background:rgba(0,0,0,.6);transform-origin:bottom;transform:scaleY(0);pointer-events:none}`;
  const el = document.createElement("style");
  el.textContent = css;
  document.head.appendChild(el);
}
